import { ReactNode, createContext, useEffect } from "react";
import useLocalStorage from "hooks/useLocalStorage";
import { useTranslation } from "react-i18next";
import useAuth from "hooks/useAuth";
import { PreferredLanguageOption, allLangs, defaultLang } from "locales/config-lang";
import _ from "lodash";
import localStorageAvailable from "utils//localStorageAvailable";
import { PROFILE_SETTING_KEYS } from "constants/profile-setting-keys";

type LocaleContextProps = {
  currentLang: PreferredLanguageOption | null;
  allLangs: Array<PreferredLanguageOption>;
  onChangeLang: (newLanguage: string) => void;
};

const initialState: LocaleContextProps = {
  currentLang: defaultLang,
  allLangs: allLangs,
  onChangeLang: (language: string): void => { },
};

const LocaleContext = createContext(initialState);

type LocaleProviderProps = {
  children: ReactNode;
};

const SPECIFIC_LANGUAGES: Array<string> = [
  "en-GB",
  "en-US"
];

function LocaleProvider({ children }: LocaleProviderProps) {
  const { i18n } = useTranslation();
  const { user } = useAuth();


  const storageAvailable = localStorageAvailable();
  let storedValue = storageAvailable ? localStorage.getItem("currentLanguageLocale") : initialState.currentLang;

  const [currentLang, setCurrentLang] = useLocalStorage("currentLanguageLocale", {
    currentLang: storedValue ?? initialState.currentLang
  });

  useEffect(() => {
    if (user) {
      const userLanguage = _.find(
        user?.profileSettings,
        (value) => value?.name === PROFILE_SETTING_KEYS.PREFERRED_LANGUAGE_CULTURE
      )?.value;

      if (userLanguage) {
        const currentLang = allLangs.find((l) => (SPECIFIC_LANGUAGES.includes(userLanguage) && l.value === userLanguage) || l.value === userLanguage.substring(0, 2));
        if (currentLang) {
          onChangeLang(currentLang.value);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onChangeLang = (value: string) => {
    i18n.changeLanguage(value);

    const newLanguage = allLangs.find((l) => l.value === value);
    setCurrentLang(newLanguage ?? defaultLang);
  };

  return (
    <LocaleContext.Provider
      value={{
        currentLang,
        allLangs,
        onChangeLang,
      }}
    >
      {children}
    </LocaleContext.Provider>
  );
}

export { LocaleProvider, LocaleContext };
